import { fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString, toEnumFromStringOrNull } from '../../../../serialization/enum-utils.js';
import { MapUtils } from '../../../../utility/map-utils.js';
import { VersionRange } from '../../../core/version-range.js';
import {
  cloneData, cloneDataOrNull, cloneListOfCloneable, DataModel, DataModelBuilder,
  isSameData, isSameList, SerializationContext, SerializedData,
} from '../../../dto/dto.js';
import { GenericOrderEntry, GenericOrderEntryStatus } from '../generic-order-entry.js';
import { OnlineTransactionDetails } from '../../online-transaction-details.js';
import { OrderSideEffect } from '../order-side-effect.js';
import { BusinessModuleRef } from '../../business-module-ref.js';
import { EventTicket, EventTicketStatus } from '../../event-ticket.js';
import { Price } from '../../price.js';
import { InvoiceVat } from '../../invoicing/invoice-vat.js';
import { EventBookingStrategy } from '../../event-booking-protocol/event-booking-strategy.js';
import { TimeZone } from '../../../../time/timezone.js';
import { Discount } from '../../discounts/discount.js';
import { Dayjs } from 'dayjs';
import { ElectronicDeliveryMethod } from '../../electronic-delivery-method.js';
import { Refund } from '../../discounts/refund.js';
import { OfflineTransactionDetails } from '../../offline-transaction-details.js';
import { TicketSupplementStatus } from '../../ticket-supplement.js';

export class GenericOrderTicketEntry extends GenericOrderEntry {
  static readonly TYPE_ID: string = 'b3db60f3c9d64ac297826fd365b6d569';
  static readonly TYPE_NAME: string = 'Tickets';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly uniqueId: string,
    public readonly orderId: string,
    public readonly entryGroupId: string | null,
    public readonly note: string | null,
    public readonly status: GenericOrderEntryStatus,
    public readonly creatorId: string,
    public readonly creationDate: Dayjs,
    public readonly eventId: string,
    public readonly bookingStrategy: EventBookingStrategy,
    public readonly onlineTransactionDetails: OnlineTransactionDetails | null,
    public readonly offlineTransactionDetails: OfflineTransactionDetails | null,
    public readonly cancelledDate: Dayjs | null,
    public readonly cancelledBy: string | null,
    public readonly sideEffects: OrderSideEffect[],
    public readonly businessRef: BusinessModuleRef,
    public readonly tickets: EventTicket[],
    public readonly deliveryMethod: ElectronicDeliveryMethod,
    public readonly refunds: Refund[],
    public applicationFee: Price | null,
    public applicationFeeVat: InvoiceVat | null,
    public discount: Discount | null,
  ) {
    super();
  }

  get $typeId(): string {
    return GenericOrderTicketEntry.TYPE_ID;
  }

  get $typeName(): string {
    return GenericOrderTicketEntry.TYPE_NAME;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, GenericOrderTicketEntry.SCHEMA_VERSION);
  }

  static create(args: {
    uniqueId: string,
    orderId: string,
    entryGroupId: string | null,
    note: string | null,
    status: GenericOrderEntryStatus,
    creatorId: string,
    creationDate: Dayjs,
    eventId: string,
    bookingStrategy: EventBookingStrategy,
    onlineTransactionDetails: OnlineTransactionDetails | null,
    offlineTransactionDetails: OfflineTransactionDetails | null,
    cancelledDate: Dayjs | null,
    cancelledBy: string | null,
    sideEffects: OrderSideEffect[],
    businessRef: BusinessModuleRef,
    tickets: EventTicket[],
    applicationFee: Price | null,
    applicationFeeVat: InvoiceVat | null,
    discount: Discount | null,
    deliveryMethod: ElectronicDeliveryMethod,
    refunds?: Refund[],
  }): GenericOrderTicketEntry {
    return new GenericOrderTicketEntry(
        args.uniqueId,
        args.orderId,
        args.entryGroupId,
        args.note,
        args.status,
        args.creatorId,
        args.creationDate,
        args.eventId,
        args.bookingStrategy,
        args.onlineTransactionDetails,
        args.offlineTransactionDetails,
        args.cancelledDate,
        args.cancelledBy,
        args.sideEffects,
        args.businessRef,
        args.tickets,
        args.deliveryMethod,
        args.refunds ?? [],
        args.applicationFee,
        args.applicationFeeVat,
        args.discount,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): GenericOrderTicketEntry {
    return GenericOrderTicketEntry.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      orderId: MapUtils.getString(map, 'orderId'),
      entryGroupId: MapUtils.getStringOrNull(map, 'entryGroupId'),
      note: MapUtils.getStringOrNull(map, 'note'),
      status: toEnumFromString(MapUtils.getString(map, '$status'), GenericOrderEntryStatus),
      creatorId: MapUtils.getString(map, 'creatorId'),
      creationDate: context.decodeDate(MapUtils.getInteger(map, 'creationDate')),
      eventId: MapUtils.getString(map, 'eventId'),
      bookingStrategy: toEnumFromString(MapUtils.getString(map, 'bookingStrategy'), EventBookingStrategy),
      onlineTransactionDetails: context.fromDataOrNull<OnlineTransactionDetails>(MapUtils.getRecordOrNull(map, 'onlineTransactionDetails')),
      offlineTransactionDetails: context.fromDataOrNull<OfflineTransactionDetails>(MapUtils.getRecordOrNull(map, 'offlineTransactionDetails')),
      cancelledDate: context.decodeDateOrNull(MapUtils.getIntegerOrNull(map, 'cancelledDate')),
      cancelledBy: MapUtils.getStringOrNull(map, 'cancelledBy'),
      sideEffects: MapUtils.getList<OrderSideEffect>(map, 'sideEffects', (value) => context.fromData<OrderSideEffect>(value as SerializedData)),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      tickets: MapUtils.getList<EventTicket>(map, 'tickets', (value) => {
        return context.fromData<EventTicket>(value as SerializedData);
      }),
      applicationFee: Price.decodeOrNull(MapUtils.getStringOrNull(map, 'applicationFee')),
      applicationFeeVat: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'applicationFeeVat'), InvoiceVat),
      discount: context.fromDataOrNull<Discount>(MapUtils.getRecordOrNull(map, 'discount')),
      deliveryMethod: toEnumFromString(MapUtils.getString(map, 'deliveryMethod'), ElectronicDeliveryMethod),
      refunds: MapUtils.getList<Refund>(map, 'refunds', (value) => {
        return context.fromData<Refund>(value as SerializedData);
      }),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'orderId': this.orderId,
      'entryGroupId': this.entryGroupId,
      'note': this.note,
      '$status': fromEnumAsString(this.status),
      'creatorId': this.creatorId,
      'creationDate': context.encodeDate(this.creationDate),
      'eventId': this.eventId,
      'bookingStrategy': fromEnumAsString(this.bookingStrategy),
      'onlineTransactionDetails': context.toDataOrNull(this.onlineTransactionDetails),
      'offlineTransactionDetails': context.toDataOrNull(this.offlineTransactionDetails),
      'cancelledDate': context.encodeDateOrNull(this.cancelledDate),
      'cancelledBy': this.cancelledBy,
      'sideEffects': this.sideEffects.map((e) => context.toData(e)),
      'businessRef': this.businessRef.encode(),
      'tickets': this.tickets.map((e) => context.toData(e)),
      'applicationFee': this.applicationFee?.encode() ?? null,
      'applicationFeeVat': fromEnumAsStringOrNull(this.applicationFeeVat),
      'discount': context.toDataOrNull(this.discount),
      'deliveryMethod': fromEnumAsString(this.deliveryMethod),
      'refunds': this.refunds.map((e) => context.toData(e)),
    };
  }

  clone(): this {
    return this.copyWith() as this;
  }

  copyWith({
    uniqueId = this.uniqueId,
    orderId = this.orderId,
    entryGroupId = this.entryGroupId,
    note = this.note,
    status = this.status,
    creatorId = this.creatorId,
    creationDate = TimeZone.cloneDate(this.creationDate),
    eventId = this.eventId,
    bookingStrategy = this.bookingStrategy,
    onlineTransactionDetails = cloneDataOrNull(this.onlineTransactionDetails),
    offlineTransactionDetails = cloneDataOrNull(this.offlineTransactionDetails),
    cancelledDate = TimeZone.cloneDateOrNull(this.cancelledDate),
    cancelledBy = this.cancelledBy,
    sideEffects = cloneListOfCloneable(this.sideEffects),
    businessRef = cloneData(this.businessRef),
    tickets = cloneListOfCloneable(this.tickets),
    applicationFee = cloneDataOrNull(this.applicationFee),
    applicationFeeVat = this.applicationFeeVat,
    discount = cloneDataOrNull(this.discount),
    deliveryMethod = this.deliveryMethod,
    refunds = cloneListOfCloneable(this.refunds),
  }: {
    uniqueId?: string,
    orderId?: string,
    entryGroupId?: string | null,
    note?: string | null,
    status?: GenericOrderEntryStatus,
    creatorId?: string,
    creationDate?: Dayjs,
    eventId?: string,
    bookingStrategy?: EventBookingStrategy,
    onlineTransactionDetails?: OnlineTransactionDetails | null,
    offlineTransactionDetails?: OfflineTransactionDetails | null,
    cancelledDate?: Dayjs | null,
    cancelledBy?: string | null,
    sideEffects?: OrderSideEffect[],
    businessRef?: BusinessModuleRef,
    tickets?: EventTicket[],
    applicationFee?: Price | null,
    applicationFeeVat?: InvoiceVat | null,
    discount?: Discount | null,
    deliveryMethod?: ElectronicDeliveryMethod,
    refunds?: Refund[],
  } = {}): this {
    return GenericOrderTicketEntry.create({
      uniqueId,
      orderId,
      entryGroupId,
      note,
      status,
      creatorId,
      creationDate,
      eventId,
      bookingStrategy,
      onlineTransactionDetails,
      offlineTransactionDetails,
      cancelledDate,
      cancelledBy,
      sideEffects,
      businessRef,
      tickets,
      applicationFee,
      applicationFeeVat,
      discount,
      deliveryMethod,
      refunds,
    }) as this;
  }

  copyAsAborted(): this {
    return this.copyWith({
      status: GenericOrderEntryStatus.Aborted,
      tickets: this.tickets.map((e) => e.copyAsAborted()),
    });
  }

  copyAsCanceled(cancelledBy: string | null, cancelledDate: Dayjs, isRefund: boolean): this {
    return this.copyWith({
      status: isRefund ? GenericOrderEntryStatus.Refunded : GenericOrderEntryStatus.Canceled,
      cancelledDate: TimeZone.cloneDateOrNull(this.cancelledDate) ?? cancelledDate,
      cancelledBy: this.cancelledBy ?? cancelledBy,
      tickets: this.tickets.map((ticket) => {
        const shouldRefund = isRefund || ticket.status === EventTicketStatus.Refunded;
        return ticket.copyAsCanceled(cancelledBy, cancelledDate, shouldRefund, { shouldCancelSupplement: false });
      }),
    });
  }

  copyWithSupplementsAsCanceled(cancelledBy: string | null, cancelledDate: Dayjs, isRefund: boolean): this {
    return this.copyWith({
      tickets: this.tickets.map((ticket) => {
        const shouldRefund = isRefund || ticket.supplement?.status === TicketSupplementStatus.Refunded;
        return ticket.copyWithSupplementAsCanceled(cancelledBy, cancelledDate, shouldRefund);
      }),
    });
  }

  copyWithOnlineTransactionDetails(transactionDetails: OnlineTransactionDetails): this {
    return this.copyWith({
      onlineTransactionDetails: transactionDetails,
    });
  }

  copyWithOfflineTransactionDetails(transactionDetails: OfflineTransactionDetails): this {
    return this.copyWith({
      offlineTransactionDetails: transactionDetails,
    });
  }

  copyWithTicketCloner(cloner: (t: EventTicket) => EventTicket): this {
    return this.copyWith({ tickets: this.tickets.map(cloner) });
  }

  isSame(other: GenericOrderTicketEntry): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      other.orderId === this.orderId &&
      other.entryGroupId === this.entryGroupId &&
      other.note === this.note &&
      other.status === this.status &&
      other.creatorId === this.creatorId &&
      TimeZone.isSameInstant(other.creationDate, this.creationDate) &&
      other.eventId === this.eventId &&
      other.bookingStrategy === this.bookingStrategy &&
      isSameData(other.onlineTransactionDetails, this.onlineTransactionDetails) &&
      isSameData(other.offlineTransactionDetails, this.offlineTransactionDetails) &&
      TimeZone.isSameInstantOrNull(other.cancelledDate, this.cancelledDate) &&
      other.cancelledBy === this.cancelledBy &&
      isSameList(other.sideEffects, this.sideEffects, isSameData) &&
      isSameData(other.businessRef, this.businessRef) &&
      isSameList(other.tickets, this.tickets, isSameData) &&
      isSameData(other.discount, this.discount) &&
      isSameData(other.applicationFee, this.applicationFee) &&
      other.applicationFeeVat === this.applicationFeeVat &&
      other.deliveryMethod === this.deliveryMethod &&
      isSameList(other.refunds, this.refunds, isSameData);
  }

  tryGetApplicationFee(): Price | null {
    return this.applicationFee;
  }

  tryGetApplicationFeeVat(): InvoiceVat | null {
    return this.applicationFeeVat;
  }

  tryGetRefunds(): Refund[] | null {
    return this.refunds;
  }

  tryAddRefund(refund: Refund): boolean {
    this.refunds.push(refund);
    return true;
  }

  tryGetEventId(): string | null {
    return this.eventId;
  }

  static verifyType(entry: DataModel): boolean {
    return entry instanceof GenericOrderTicketEntry;
  }

  static assertType(entry: DataModel): entry is GenericOrderTicketEntry {
    return entry instanceof GenericOrderTicketEntry;
  }
}

export class GenericOrderTicketEntryBuilder extends DataModelBuilder<GenericOrderTicketEntry> {
  build(context: SerializationContext, map: SerializedData): GenericOrderTicketEntry {
    return GenericOrderTicketEntry.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    this.migrateEnum(map, '$status', GenericOrderEntryStatus, GenericOrderEntryStatus.Open);

    if(versionRange.contains(GenericOrderTicketEntry.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(GenericOrderTicketEntry.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In version 2 we added `offlineTransactionDetails` of type `OfflineTransactionDetails | null`.
    if(version < 2) version = 2;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
